import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react'
import { ChevronRight, MoreHorizontal, Plus, Search } from 'lucide-react'
import {
  Banner, Button, Card, Field, IconButton, Row, Segmented, Sheet, useToast,
} from '../ui'
import {
  refetchFailed, useAttention, useCommitments, useEntryState, useFeed, useListItems, useListRuns, useLists,
} from '../lib/queries'
import { needsYou, type AttentionItem } from '../lib/attention'
import { replace, useOnTab, useRoute } from '../lib/router'
import { recipeFromQuery } from '../lib/recipeLines'
import { useAddItem, useAddList, useReorderLists } from '../lib/mutations'
import { addTask, parseTask } from '../lib/listAdd'
import { taskFromQuery } from '../lib/taskLink'
import { primaryTaskList, shortDay } from '../lib/taskRules'
import { attempt, useWriteError } from '../lib/useAction'
import { listSummary } from '../lib/listSummary'
import { KindIcon } from '../components/KindIcon'
import { ScreenHead } from '../components/ScreenHead'
import { ListMenu } from '../components/ListMenu'
import { CHIP_GROUP, ListChips, SHORTLIST_CHIP } from '../components/ListChips'
import { QuickAdd } from '../components/QuickAdd'
import type { List, ListItem, ListKind, ListRun } from '../lib/types'
import { Loading, Failed } from './Stub'
import { ShortlistBody } from '../components/ShortlistBody'
import { ListBody } from '../components/ListBody'
import { KindPicker } from '../components/KindPicker'
import { listGroupOf } from '../lib/listKinds'
import { fillSlots, moveRelative, moveTo } from '../lib/reorder'
import { chipOrder } from '../lib/chipOrder'
import { usePointerReorder } from '../lib/usePointerReorder'
import { userKey } from '../lib/userStorage'

const FLAG_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'seen', label: 'Seen' },
  { value: 'visited', label: 'Visited' },
  { value: 'read', label: 'Read' },
]

/** One identity for "nothing yet", so a `useMemo` downstream does not
 *  recompute every render on a fresh empty array. */
const NONE_YET: never[] = []

/** L1: which sentence-case group a non-pinned, non-archived list falls into.
 *  `listKinds.ts` holds it, so Add to list orders by the same rule. */
const groupOf = listGroupOf

/** The section a list is drawn in, which is the set it can be reordered
 *  within: a drag never moves a list between Pinned and Doing. */
function groupKey(l: List): string {
  if (l.archived_at) return 'archived'
  if (l.pinned) return 'pinned'
  return groupOf(l.kind)
}

/**
 * The Shortlist is the page (Lists, direction B): the chip row of utility
 * lists, then `ShortlistBody` inline — there is no row to open it from and no
 * route of its own on the phone.
 *
 * "Shortlist" is still not a list. Underneath it is `entry_state`, which is
 * the taste signal store and feeds scoring, and does not belong in
 * `list_items`, which is why it isn't one of `lists`. It used to be drawn as
 * a pseudo list the route could open (`#/lists/shortlist`); that id is now an
 * alias for the index, which already shows it. The desk keeps its own
 * Shortlist pane and the same id (`lib/deskList.ts`), so a link copied from
 * there still lands somewhere sensible here. The alias only works because a
 * real list id is a Postgres UUID and can never collide with the literal.
 */
const SHORTLIST_ALIAS = SHORTLIST_CHIP

/** The chip last selected, for the tab session: leaving for Today and coming
 *  back to a bare `#/lists` finds the same list under the row. A hash with an
 *  id wins over it. `sessionStorage` and not `localStorage` — that one holds
 *  the signed-in session (CLAUDE.md rule 7) — and every access is guarded,
 *  since it throws in a private window and with site data blocked, where the
 *  Shortlist is simply what a bare hash shows. */
const CHIP_KEY = 'kiez.lists.chip'
function readChip(): string {
  try { return sessionStorage.getItem(userKey(CHIP_KEY)) || SHORTLIST_CHIP } catch { return SHORTLIST_CHIP }
}
function writeChip(id: string) {
  try { sessionStorage.setItem(userKey(CHIP_KEY), id) } catch { /* the chip just is not remembered */ }
}

/**
 * @param openId  The list the route is pointing at, from `#/lists/<id>`.
 *                Doug: "When i click on the list from the homepage it should
 *                open that list not the list tab."
 *
 * The chip row is a view switch (Doug: "the chips are a way to toggle the
 * view, not a link to a new page"). Which view is under it is the hash —
 * `#/lists` the Shortlist, `#/lists/<id>` that list — and a chip is written to
 * it with `replace`, never `go`: a tap swaps the body in place and Back leaves
 * the tab rather than stepping back through every chip Doug tried. The hash
 * is the one answer; `remembered` only fills in when it has none (a bare
 * `#/lists` after a trip to Today), and is never read when it has one.
 */
export function Lists({ openId }: {
  openId: string | null
}) {
  const onTab = useOnTab('lists')
  const [remembered, setRemembered] = useState(readChip)
  const [newList, setNewList] = useState(false)
  // P3: the header's "+" is Quick add, and its search is an icon. Neither is
  // remembered: leaving the tab closes the field (below), and the sheet is
  // mounted only while open.
  const [quickAdd, setQuickAdd] = useState(false)
  const [searchOpen, setSearchOpen] = useState(false)
  const [menuFor, setMenuFor] = useState<List | null>(null)
  const [archivedOpen, setArchivedOpen] = useState(false)
  const [query, setQuery] = useState('')
  // B2, the Shortcut route: `#/lists?recipe=<text>` opens the grocery list
  // with the recipe sheet filled. Held here once read, and the hash rewritten
  // without it, so a reload or Back does not open the sheet a second time.
  const route = useRoute()
  const [recipe, setRecipe] = useState<string | null>(null)

  const listsQ = useLists()
  const itemsQ = useListItems()
  const runsQ = useListRuns()
  const feedQ = useFeed()
  const stateQ = useEntryState()
  const commitmentsQ = useCommitments()
  const lists = listsQ.data ?? NONE_YET
  const items = itemsQ.data ?? NONE_YET
  const runs = runsQ.data ?? NONE_YET
  // a2-20: what the tab badge counts, item by item (`useAttentionCount` is
  // this list's length). The line below names only the tasks in it, and the
  // two now count different things ON PURPOSE: a saved event inside the 48
  // hours is a row on this very page, leading with its when-phrase
  // ("Tonight 19:50", `ShortlistBody`), so naming it again in a sentence
  // above would say it twice; the badge sits on the tab and is read from
  // every other screen, where the page is not in view, so it still counts
  // the events. Do not "fix" one to match the other.
  const attention = useAttention()
  const tasksDue = useMemo(
    () => attention.filter((a): a is Extract<AttentionItem, { kind: 'task' }> => a.kind === 'task'),
    [attention],
  )

  // Above the guards. A hook below an early return does not run on the first
  // render, which is the bug that once left a whole screen recorded as done
  // without ever having drawn.
  const reorderM = useReorderLists()
  const addListM = useAddList()
  const writeError = useWriteError(reorderM, addListM)

  // The Shortlist chip's count is what `ShortlistBody` draws as "N saved": a
  // saved `entry_state` row whose event is in the feed.
  const savedCount = useMemo(() => {
    const ids = new Set((feedQ.data ?? NONE_YET).map(e => e.id))
    return (stateQ.data ?? NONE_YET).filter(r => r.state === 'saved' && ids.has(r.entry_id)).length
  }, [feedQ.data, stateQ.data])

  // Which view is under the chip row. The hash wins; `remembered` answers only
  // a bare `#/lists`; a list that is not there (deleted, a stale bookmark)
  // is not a choice, and the Shortlist is what is left. Computed above the
  // guards because the effects below read it, and while `lists` is still
  // empty it is the Shortlist, which nothing acts on until `pending` is over.
  const isView = (id: string) => id === SHORTLIST_CHIP || lists.some(l => l.id === id)
  const selectedId = openId ? (isView(openId) ? openId : SHORTLIST_CHIP)
    : isView(remembered) ? remembered : SHORTLIST_CHIP
  const selectedList = lists.find(l => l.id === selectedId) ?? null

  /** Switch the view in place. `remembered` is set before the hash is
   *  rewritten so the render the rewrite causes already agrees with it. */
  const select = (id: string) => {
    setRemembered(id)
    writeChip(id)
    replace('lists', id === SHORTLIST_CHIP ? null : id)
  }

  // Tasks plan D9, the Shortcut route: `#/lists?task=<text>` adds a task to
  // the first task list through `addTask` (so "by Friday" and "every week"
  // parse as typed), opens that list and rewrites the hash without the text.
  // `taskHandled` keeps a re-render between the add and the rewrite from
  // adding it twice.
  const addTaskM = useAddItem()
  const toast = useToast()
  const taskHandled = useRef<string | null>(null)
  useEffect(() => {
    const text = taskFromQuery(route.query)
    if (!text || !lists.length || taskHandled.current === text) return
    taskHandled.current = text
    const target = primaryTaskList(lists)
    if (!target) { toast({ text: 'No task list yet' }); replace('lists', null); return }
    const parsed = parseTask(text)
    void addTask({ addM: addTaskM }, target, items.filter(i => i.list_id === target.id), text).then(out => {
      if (!out.ok) toast({ text: `Couldn't add "${parsed.body}"` })
      else if (!out.empty) toast({ text: `Added "${parsed.body}"${parsed.due_on ? ` · due ${shortDay(parsed.due_on)}` : ''}` })
    })
    select(target.id)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.query.task, lists.length])

  useEffect(() => {
    const got = recipeFromQuery(route.query)
    if (!got || !lists.length) return
    const grocery = lists.filter(l => l.kind === 'grocery' && !l.archived_at).sort((a, b) => Number(b.pinned) - Number(a.pinned))[0]
    setRecipe('text' in got ? got.text : 'clipboard')
    if (grocery) select(grocery.id)
    else replace('lists', null)
    // `select` is stable enough for this: it closes over state setters and
    // `replace`, and the effect re-runs only when the query or lists change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [route.query.recipe, lists.length])

  const itemsByList = useMemo(() => {
    const by = new Map<string, ListItem[]>()
    for (const i of items) {
      const list = by.get(i.list_id)
      if (list) list.push(i); else by.set(i.list_id, [i])
    }
    return by
  }, [items])

  const runsByList = useMemo(() => {
    const by = new Map<string, ListRun[]>()
    for (const r of runs) {
      const list = by.get(r.list_id)
      if (list) list.push(r); else by.set(r.list_id, [r])
    }
    return by
  }, [runs])

  // Long-press then drag, or hold and let go for the list's menu. The hook is
  // shared with a list's own items. A chip goes anywhere in the row, on the
  // side of the target the finger is on, and the positions written are the
  // whole set's (`fillSlots` keeps the archived lists' places); the archived
  // rows below are a column and still swap into the slot they are dropped on.
  const drag = usePointerReorder({
    onDrop: (dragId, overId, group, side) => {
      const all = lists.map(l => l.id)
      if (group === CHIP_GROUP) {
        const next = moveRelative(chipOrder(lists).map(l => l.id), dragId, overId, side ?? 'before')
        if (next) reorderM.mutate(fillSlots(all, next))
        return
      }
      const order = moveTo(all, dragId, overId)
      if (order) reorderM.mutate(order)
    },
    onHold: id => { const l = lists.find(x => x.id === id); if (l) setMenuFor(l) },
  })

  const pending = listsQ.isPending || itemsQ.isPending || runsQ.isPending || feedQ.isPending
    || stateQ.isPending || commitmentsQ.isPending
  const error = listsQ.error ?? itemsQ.error ?? runsQ.error ?? feedQ.error ?? stateQ.error ?? commitmentsQ.error

  // A route can point at a list that no longer exists — a bookmark, or a
  // list deleted on another device. Before this, the index rendered as the
  // right fallback but the URL kept the bogus id, so back and reload both
  // landed here again (UI-audit F25). `replace`: it corrects the URL Doug
  // followed, and a pushed entry would trap Back on the bogus one.
  useEffect(() => {
    if (pending || error) return
    if (openId && !isView(openId)) replace('lists', null)
  }, [openId, pending, error, lists])  // eslint-disable-line react-hooks/exhaustive-deps

  // `#/lists/shortlist` is the Shortlist's old address. Rewritten in place
  // (`replace`) rather than pushed: this is a correction of the URL Doug
  // followed, not a second place — a pushed entry would trap Back, whose
  // previous entry is the alias again, which rewrites forward again. Not
  // gated on `pending`: the alias needs no list to be judged by. It also
  // takes the memory with it, so a bare hash afterwards is still the Shortlist.
  useEffect(() => {
    if (openId !== SHORTLIST_ALIAS) return
    setRemembered(SHORTLIST_CHIP)
    writeChip(SHORTLIST_CHIP)
    replace('lists', null)
  }, [openId])

  // Keep the memory and the hash in step with the view on screen. A hash that
  // names a list is what was chosen, so it becomes the memory (a link from
  // Today, a reload). A bare hash over a remembered list is a visit that came
  // back without one — the tab bar — and the hash is rewritten to say what is
  // drawn, so the URL never names the Shortlist over a list. Only while the
  // tab is shown: a hidden pane is handed `openId = null` whatever the hash is.
  useEffect(() => {
    if (!onTab || pending || error) return
    if (openId && isView(openId) && openId !== SHORTLIST_ALIAS) {
      if (openId !== remembered) { setRemembered(openId); writeChip(openId) }
    } else if (!openId && selectedId !== SHORTLIST_CHIP) {
      replace('lists', selectedId)
    }
  }, [onTab, pending, error, openId, remembered, selectedId])  // eslint-disable-line react-hooks/exhaustive-deps

  // An archived list that is selected has no chip, so its row in the archived
  // group is the only place that says which one it is: open the group.
  const selectedArchived = !!selectedList?.archived_at
  useEffect(() => { if (selectedArchived) setArchivedOpen(true) }, [selectedArchived, selectedId])

  // The search field is a visit's, not a setting: a hidden pane stays
  // mounted (`App.tsx`), so state would otherwise survive a trip to Today.
  useEffect(() => {
    const onHash = () => {
      if (!window.location.hash.startsWith('#/lists')) { setSearchOpen(false); setQuery('') }
    }
    window.addEventListener('hashchange', onHash)
    return () => window.removeEventListener('hashchange', onHash)
  }, [])

  const closeSearch = () => { setSearchOpen(false); setQuery('') }

  // a2-30: the head stays on a failed read — the account button is the way
  // to the System view — and Retry re-reads only the queries that failed.
  // On a failed read there is nothing to search or add to, so the head keeps
  // its old single "+", which starts a list.
  const failedHead = (
    <ScreenHead
      className="lists-head"
      lead={<h1 className="screen-title">Lists</h1>}
      trailing={<IconButton label="New list" onClick={() => setNewList(true)}><Plus size={20} /></IconButton>}
    />
  )
  const head = (
    <ScreenHead
      className="lists-head"
      lead={<h1 className="screen-title">Lists</h1>}
      trailing={
        <>
          <IconButton label="Search lists" aria-expanded={searchOpen}
            onClick={() => (searchOpen ? closeSearch() : setSearchOpen(true))}>
            <Search size={20} aria-hidden="true" />
          </IconButton>
          <IconButton label="Add" onClick={() => setQuickAdd(true)}>
            <Plus size={20} aria-hidden="true" />
          </IconButton>
        </>
      }
    />
  )
  const newListSheet = newList && (
    <NewListForm
      onClose={() => setNewList(false)}
      onSave={async (name, kind, flagLabel) => {
        if (await attempt(addListM.mutateAsync({ name, kind, flagLabel }))) setNewList(false)
      }}
    />
  )

  if (pending) return <Loading />
  if (error) {
    return (
      <>
        {failedHead}
        {writeError && <Banner tone="bad">{writeError}</Banner>}
        <Failed error={error.message} onRetry={() => void refetchFailed()} />
        {newListSheet}
      </>
    )
  }

  const itemsOf = (id: string) => itemsByList.get(id) ?? NONE_YET
  // A search result opens its list in place like a chip does — and ends the
  // search, or the results would stay over the body the tap just chose.
  const openFromSearch = (id: string) => { closeSearch(); select(id) }

  const trimmed = query.trim().toLowerCase()
  const searching = trimmed.length > 0

  const active = lists.filter(l => !l.archived_at)
  const archived = lists.filter(l => l.archived_at)

  const runsOf = (id: string) => runsByList.get(id) ?? NONE_YET

  const listRow = (l: List) => {
    const summary = listSummary(l, itemsOf(l.id), runsOf(l.id), new Date())
    return (
      <div key={l.id} className={`${drag.classFor(l.id)}${l.id === selectedId ? ' is-selected' : ''}`}
        aria-current={l.id === selectedId ? 'true' : undefined} {...drag.bind(l.id, groupKey(l))}>
        <Card>
          <Row
            className="u-row--list"
            leading={summary.ring
              ? <Ring done={summary.ring.done} total={summary.ring.total} />
              : <span className="lists-tile"><KindIcon kind={l.kind} /></span>}
            title={l.name}
            meta={summary.meta}
            trailing={
              <IconButton label={`More on "${l.name}"`} size="sm" onClick={() => setMenuFor(l)}>
                <MoreHorizontal size={18} aria-hidden="true" />
              </IconButton>
            }
            onPress={() => select(l.id)}
          />
        </Card>
      </div>
    )
  }

  return (
    <>
      {head}
      {writeError && <Banner tone="bad">{writeError}</Banner>}

      {tasksDue.length > 0 && <NeedsYou items={tasksDue} onOpenTask={select} />}

      {/* N3. Searches list names and every item body across lists; a list
          match and an item match both open the list — an item on its own has
          nowhere else to be shown yet. */}
      {searchOpen && (
        <form className="lists-search" role="search" onSubmit={ev => ev.preventDefault()}>
          <Field.Input
            type="search"
            autoFocus
            value={query}
            onChange={ev => setQuery(ev.currentTarget.value)}
            onKeyDown={ev => { if (ev.key === 'Escape') { ev.preventDefault(); closeSearch() } }}
            aria-label="Search lists"
            placeholder="Search lists"
          />
          <Button variant="quiet" onClick={closeSearch}>Cancel</Button>
        </form>
      )}

      {searching ? (
        <div className="lists-results">
          <SearchResults query={trimmed} lists={active} items={items} onOpen={openFromSearch} />
        </div>
      ) : (
        <>
          <ListChips
            lists={lists}
            itemsOf={itemsOf}
            runsOf={runsOf}
            drag={drag}
            archivedCount={archived.length}
            archivedOpen={archivedOpen}
            onToggleArchived={() => setArchivedOpen(o => !o)}
            onOpen={select}
            selectedId={selectedId}
            shortlistCount={savedCount}
            visible={onTab}
          />

          {/* The archived chip in the row above toggles these. */}
          {archived.length > 0 && archivedOpen && (
            <div>
              <p className="lists-label">Archived</p>
              <div className="lists-group">{archived.map(listRow)}</div>
            </div>
          )}

          {/* The view under the row. Keyed by list so two lists of one kind do
              not share a component's state (a half-typed add field, an open
              sheet) across a chip tap. No transition: the swap is the tap. */}
          {selectedList
            ? <ListBody key={selectedList.id} list={selectedList} items={itemsOf(selectedList.id)} recipe={recipe} onRecipeDone={() => setRecipe(null)} />
            : <ShortlistBody variant="inline" />}
        </>
      )}

      {newListSheet}
      {quickAdd && (
        <QuickAdd
          lists={lists}
          items={items}
          onClose={() => setQuickAdd(false)}
          onNewList={() => { setQuickAdd(false); setNewList(true) }}
        />
      )}
      {menuFor && <ListMenu list={menuFor} onClose={() => setMenuFor(null)} />}
    </>
  )
}

/**
 * a2-20: the tab badge's referent, for the tasks in it. "1 needs you: Pay
 * rent (due today)" — each task named and tappable, opening its list. Drawn
 * only when a task is due. Saved events inside 48 hours are not named here:
 * they are rows below, led by their when-phrase (see `tasksDue` above for why
 * this and the badge count differently).
 */
function NeedsYou({ items, onOpenTask }: {
  items: Extract<AttentionItem, { kind: 'task' }>[]
  onOpenTask: (listId: string) => void
}) {
  return (
    <p className="lists-attn">
      <b>{needsYou(items.length)}:</b>{' '}
      {items.map((it, n) => (
        <span key={it.id}>
          {n > 0 && ', '}
          <button type="button" className="lists-attn__item" onClick={() => onOpenTask(it.listId)}>
            {it.title} <span className="lists-attn__why">({it.why})</span>
          </button>
        </span>
      ))}
    </p>
  )
}

/** The template row's leading slot: a 44px conic-gradient ring rather than
 *  the kind's icon — L5's own progress, one glance before the row is even
 *  open. Purely visual; `aria-label` carries the fraction `Progress`'s bar
 *  would otherwise say in words. */
function Ring({ done, total }: { done: number; total: number }) {
  const pct = total > 0 ? Math.round((done / total) * 100) : 0
  return (
    <span
      className="lists-ring"
      role="img"
      aria-label={`${done} of ${total} done`}
      style={{ '--pct': `${pct}%` } as CSSProperties}
    />
  )
}

function SearchResults({ query, lists, items, onOpen }: {
  query: string; lists: List[]; items: ListItem[]; onOpen: (id: string) => void
}) {
  const byId = useMemo(() => new Map(lists.map(l => [l.id, l])), [lists])
  const matchedLists = lists.filter(l => l.name.toLowerCase().includes(query))
  const matchedItems = items.filter(i => byId.has(i.list_id) && i.body.toLowerCase().includes(query))

  if (matchedLists.length === 0 && matchedItems.length === 0) {
    return <div className="stub">Nothing matches "{query}".</div>
  }

  return (
    <div className="lists-group">
      {matchedLists.map(l => (
        <Card key={l.id}>
          <Row
            className="u-row--list"
            leading={<span className="lists-tile"><KindIcon kind={l.kind} /></span>}
            title={l.name}
            meta="List"
            trailing={<ChevronRight size={18} aria-hidden="true" />}
            onPress={() => onOpen(l.id)}
          />
        </Card>
      ))}
      {matchedItems.map(i => {
        const list = byId.get(i.list_id)!
        return (
          <Card key={i.id}>
            <Row
              className="u-row--list"
              leading={<span className="lists-tile"><KindIcon kind={list.kind} /></span>}
              title={i.body}
              meta={`in ${list.name}`}
              trailing={<ChevronRight size={18} aria-hidden="true" />}
              onPress={() => onOpen(list.id)}
            />
          </Card>
        )
      })}
    </div>
  )
}

function NewListForm({ onClose, onSave }: {
  onClose: () => void
  onSave: (n: string, k: ListKind, flag: string | null) => void
}) {
  const [name, setName] = useState('')
  const [kind, setKind] = useState<ListKind>('task')
  const [flag, setFlag] = useState('')
  return (
    <Sheet open onOpenChange={o => { if (!o) onClose() }} title="New list"
      actions={
        <>
          <Button variant="primary" disabled={!name.trim()}
            onClick={() => onSave(name.trim(), kind, flag || null)}>Create</Button>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
        </>
      }>
      <div className="lists-form">
        <Field label="Name">
          <Field.Input value={name} placeholder="Books" autoFocus onChange={e => setName(e.target.value)} />
        </Field>
        <Field label="Kind">
          <KindPicker value={kind} onChange={setKind} />
        </Field>
        {kind === 'collection' && (
          <Field label="Flag">
            <div className="lists-segscroll">
              <Segmented value={flag} onChange={setFlag} options={FLAG_OPTIONS} />
            </div>
          </Field>
        )}
      </div>
    </Sheet>
  )
}
